<template>
  <v-window-item class="overflow-y-auto fill-height">
    <div
      v-if="reinforcementInput && reinforcementInput.id > 0"
      class="d-flex fill-height flex-column overflow-y-auto"
    >
      <div class="pa-3 pb-0">
        <ActionBar
          :disabled="isLocked"
          show-import
          :disabled-import="schematisationStagesToImport.length === 0"
          @import="toggleImportDialog(true)"
          show-save
          @save="updateOption"
          :loading-save="saveLoading"
          show-save-and-generate
          @save-generate="toggleDialog"
          :disabled-save-and-generate="saveLoading"
          :loading-save-and-generate="generateLoading"
        />
      </div>
      <v-container>
        <v-row density="compact" class="fill-height">
          <v-col sm="12" md="6">
            <v-text-field
              variant="underlined"
              color="primary"
              :model-value="reinforcementInput.name"
              @update:model-value="onValueChanged({ parameter: 'name', value: $event })"
              :class="[getDirtyClass(reinforcementInput, 'name'), isLocked ? 'disable-events' : '']"
              label="Code/name reinforcement option"
              hide-details
            />
          </v-col>
          <v-col sm="12" md="6">
            <DropDownSoilType
              :items="getBaseSoilTypeItemsByRevision(currentStabSchematisationStage.soiltyperevision)"
              :item="reinforcementInput.base_soil_type"
              label="Soil type reinforcement"
              @set="(value) => onValueChanged({ parameter: 'base_soil_type', value: value })"
              :dirtyClass="getDirtyClass(reinforcementInput, 'base_soil_type')"
              :class="[isLocked ? 'disable-events' : '']"
            />
          </v-col>
        </v-row>
      </v-container>
      <v-container class="overflow-y-auto">
        <DikeReinforcementForm ref="dikeFormRef" />
        <InnerBermReinforcementForm ref="innerBermFormRef" />
        <OuterBermReinforcementForm ref="outerBermFormRef" />
      </v-container>
      <ImportFromPreviousStageDialog
        :schematisation-stages="schematisationStagesToImport"
        v-model="importDialog"
        :loading="importLoading"
        text="reinforcement generate option"
        @close="toggleImportDialog(false)"
        @import="handleImport"
      />
      <GenerateReinforcementsDialog
        :value="generateDialog"
        :loading="generateLoading"
        @generate="generateReinforcements"
        @close="toggleDialog"
      />
    </div>
  </v-window-item>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import DirtyFieldsMixIn from '@/mixins/DirtyFieldsMixIn.vue'
import DikeReinforcementForm from '@/components/reinforcement/DikeReinforcementForm.vue'
import InnerBermReinforcementForm from '@/components/reinforcement/InnerBermReinforcementForm.vue'
import OuterBermReinforcementForm from '@/components/reinforcement/OuterBermReinforcementForm.vue'
import ImportFromPreviousStageDialog from '@/components/schematisationDetail/ImportFromPreviousStageDialog.vue'
import ActionBar from '@/components/common/ActionBar.vue'
import GenerateReinforcementsDialog from '@/components/reinforcement/GenerateReinforcementsDialog.vue'
import DropDownSoilType from '@/components/form/DropDownSoilType.vue'

export default {
  name: 'GenerateTab',
  components: {
    DropDownSoilType,
    GenerateReinforcementsDialog,
    ActionBar,
    ImportFromPreviousStageDialog,
    OuterBermReinforcementForm,
    InnerBermReinforcementForm,
    DikeReinforcementForm
  },
  mixins: [DirtyFieldsMixIn],
  computed: {
    ...mapState('reinforcementGenerate', {
      reinforcementInput: (state) => state.currentItem,
      importLoading: (state) => state.importLoading,
      importDialog: (state) => state.importDialog,
      generateLoading: (state) => state.generateLoading,
      generateDialog: (state) => state.dialog,
      saveLoading: (state) => state.writeLoading
    }),
    ...mapState('stabSchematisationStage', {
      currentStabSchematisationStage: (state) => state.currentItem
    }),
    ...mapGetters('reinforcementGenerate', {
      slopeChoices: 'getSlopeChoices',
      startingPointChoices: 'getStartingPointChoices'
    }),
    ...mapGetters('baseSoilType', {
      getBaseSoilTypeItemsByRevision: 'getItemsByRevision'
    }),
    ...mapGetters('stabSchematisation', {
      isLocked: 'isLockedCurrent'
    }),
    ...mapGetters('stabSchematisationStage', {
      schematisationStagesToImport: 'previousSchematisationStagesHaveReinforcement'
    })
  },
  methods: {
    ...mapActions('reinforcementGenerate', {
      setNestedParameter: 'setNestedParameter',
      updateGenerateOption: 'updateGenerateOption',
      fetchCrestLevelAndSettlement: 'fetchCrestLevelAndSettlement',
      toggleImportDialog: 'toggleImportDialog',
      importReinforcementGenerateOptionFromStage: 'importReinforcementGenerateOptionFromStage',
      generateReinforcement: 'generateReinforcement',
      toggleDialog: 'toggleDialog',
      toggleWriteLoading: 'toggleWriteLoading'
    }),
    onValueChanged({ parameter, value }) {
      this.addDirty(this.reinforcementInput, parameter)
      this.setNestedParameter({
        item: this.reinforcementInput,
        parameter: parameter,
        value
      })
    },
    async updateOption() {
      try {
        this.toggleWriteLoading()
        await this.updateGenerateOption()
        this.cleanUpDirtyItems()
      } catch (e) {
        console.log(e)
      } finally {
        this.toggleWriteLoading()
      }
    },
    cleanUpDirtyItems() {
      const formRefs = [
        this.$refs.dikeFormRef,
        this.$refs.innerBermFormRef,
        this.$refs.outerBermFormRef
      ]
      if (formRefs.every(Boolean)) {
        for (const formRef of formRefs) {
          formRef.emptyDirtyItemsClass()
        }
      }
      this.dirtyItems = []
    },
    async handleImport(sourceSchematisationStageId) {
      try {
        this.cleanUpDirtyItems()
        await this.importReinforcementGenerateOptionFromStage({
          sourceSchematisationStageId,
          targetSchematisationStageId: this.currentStabSchematisationStage.id
        })
      } catch (e) {
        console.log(e)
      } finally {
        this.toggleImportDialog(false)
      }
    },
    async generateReinforcements() {
      try {
        await this.updateOption()
        await this.generateReinforcement(this.currentStabSchematisationStage.id)
      } catch (e) {
        console.log(e)
      } finally {
        this.toggleDialog()
      }
    }
  }
}
</script>

<style scoped>
.v-input {
  font-size: 14px;
}

.text-label {
  height: 100%;
  display: flex;
  align-items: flex-end;
  font-size: 14px;
}
</style>
